/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { StationInfoView } from './models/station-info.view';
import { RequestState } from '@shared/models/request-state';
import { Observable, map } from 'rxjs';
import { DecsService } from '../decs/decs.service';
import { AlertService } from '../alert/alert.service';
import { DateTime } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class StationInfoStore {
  constructor(
    private decsService: DecsService,
    private alertService: AlertService
  ) {}

  /** Gets station timezone and elevation via Sabre JS* command. */
  getStationInfo(
    station: string,
    isRefresh = false
  ): Observable<RequestState<StationInfoView>> {
    const cmd = `JS*${station}`;

    return this.decsService.submitDecsRequest({ cmd }, isRefresh).pipe(
      map((response) => {
        if (response.hasError) {
          this.showErrorAlert();
        }

        if (!response.response) {
          return response.into<StationInfoView>();
        }

        const sabreText = response.response.result;

        return response.into<StationInfoView>(
          this.parseSabreText(station, sabreText, DateTime.now())
        );
      })
    );
  }

  parseSabreText(
    station: string,
    text: string,
    timestamp: DateTime
  ): StationInfoView {
    // Example response:
    //     JSUDEN         STATION DATA
    // NAME<DENVER                            ACARS DEN   BAG CNX<0090
    // ICAO KDEN  ICODE K2  LAT  39.51.7N    LONG 104.40.3W
    // AA STATION<Y       CHO SYM<DENJ  FIR/UIR KZDV GRWY<N  LRWY 160
    // ARPT<Y COTRM<N INTL<N WSS<Y TPS<Y NWB<N NDA<N MTC<2 CAT-3A<Y
    // TIME ZONE ELEV  MAG VAR         FERRY FUEL
    // <M07.0<M <5434  <008<E   TC<N   IN<  OUT<    DD<Y DD TYPE<A
    // DST Y  FUEL DENSITY<6.67 HLD<30  FAR 36<N  START<0000  END<0000
    // AIRPORT/CLSD-START<0000 END<0000 TOWER/CLSD-START<0000 END<0000
    // GRND HNDLR  <AA                                       SEND FP<N
    // ALTN MIN<                                         RWY<
    // RECOMMENDED VHF NAVAID        HLD N/R<35  ALT<20  EDA<N ALTN<Y
    // NAME DENVER INTL                     ATA DEN   EXP  FC<7  DI<
    // AUTHORIZED TAKEOFF AND LANDING - ACFT GROUP
    // OPERATION  1  2  3  4  5  6  7  8  9 10 11 12
    // SCHEDULED <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // CHARTER   <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // DRIFTDOWN <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // ALTERNATE <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // OPERATION 13 14 15 16 17 18 19 20 21 22 23 24
    // SCHEDULED <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // CHARTER   <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // DRIFTDOWN <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // ALTERNATE <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // <<<<<
    // 1550 1193* 1554 1197

    //     const testString = `
    //     JSUPHX         STATION DATA
    // NAME<PHOENIX       AZ                  ACARS PHX   BAG CNX<0090
    // ICAO KPHX  ICODE K2  LAT  33.26.0N    LONG 112.00.6W
    // AA STATION<Y       CHO SYM<PHX2  FIR/UIR KZAB GRWY<N  LRWY 114
    // ARPT<Y COTRM<N INTL<N WSS<Y TPS<Y NWB<N NDA<N MTC<1 CAT-3A<N
    // TIME ZONE ELEV  MAG VAR         FERRY FUEL
    // <M07.0<S <1135  <012<E   TC<N   IN<  OUT<    DD<Y DD TYPE<A
    // DST N  FUEL DENSITY<6.60 HLD<15  FAR 36<N  START<0000  END<0000
    // AIRPORT/CLSD-START<0000 END<0000 TOWER/CLSD-START<0000 END<0000
    // GRND HNDLR  <AA                                       SEND FP<N
    // ALTN MIN<                                         RWY<
    // RECOMMENDED VHF NAVAID PXR    HLD N/R<25  ALT<15  EDA<N ALTN<Y
    // NAME PHOENIX SKY HARBOR INTL         ATA PHX   EXP  FC<7  DI<
    // AUTHORIZED TAKEOFF AND LANDING - ACFT GROUP
    // OPERATION  1  2  3  4  5  6  7  8  9 10 11 12
    // SCHEDULED <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // CHARTER   <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // DRIFTDOWN <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // ALTERNATE <Y <Y <Y <N <Y <Y <N <Y <Y <N <N <Y
    // OPERATION 13 14 15 16 17 18 19 20 21 22 23 24
    // SCHEDULED <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // CHARTER   <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // DRIFTDOWN <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // ALTERNATE <Y <Y <Y <Y <Y <Y <N <N <N <N <N <N
    // <<<<<
    // 1202 1201* 1206 1205
    //     `;

    const namePattern = /NAME<(?<name>.+)ACARS/;
    const icaoLatLongPattern =
      /ICAO (?<icao>.{4}).*LAT(?<lat>.*)LONG(?<long>.*)/;
    const timeZoneElevPattern =
      /<(?<timeZone>[M|P]\d{2}\.\d{1})<[A-Z]{1}\s<(?<elevation>.{5})/;

    const nameMatch = text.match(namePattern);
    let name = '';

    if (nameMatch) {
      name = nameMatch.groups?.['name'] || '';
    }

    const icaoLatLongMatch = text.match(icaoLatLongPattern);
    let icao = '';
    let latitude = 0;
    let longitude = 0;

    if (icaoLatLongMatch) {
      icao = icaoLatLongMatch.groups?.['icao'] || '';
      const latString = icaoLatLongMatch.groups?.['lat'] || '';
      const longString = icaoLatLongMatch.groups?.['long'] || '';

      const decimalCoords = this.convertCoordsToDec(latString, longString);
      latitude = decimalCoords.latitude;
      longitude = decimalCoords.longitude;
    }

    const timeZoneElevMatch = text.match(timeZoneElevPattern);
    let elevation = 0;

    if (timeZoneElevMatch) {
      elevation = parseInt(timeZoneElevMatch.groups?.['elevation'] || '-9999');
    }

    return {
      timestamp,
      station,
      icao,
      elevation,
      name,
      latitude,
      longitude,
    };
  }

  /** Convert from minutes/seconds to decimal for map. */
  private convertCoordsToDec(
    lat: string,
    long: string
  ): { latitude: number; longitude: number } {
    let latitude = 0;
    let longitude = 0;

    // Convert latitude from 32.53.8N to decimal.
    const latParts = lat.replace('N', '').replace('S', '').split('.');

    if (latParts.length === 3) {
      latitude =
        parseInt(latParts[0]) +
        this.convertMinSecToDecimal(
          parseInt(latParts[1]),
          parseInt(latParts[2])
        );

      // South lats are negative.
      if (lat.includes('S')) {
        latitude *= -1;
      }
    }

    // Convert longitude from 115.09.1W to decimal.
    const longParts = long.replace('E', '').replace('W', '').split('.');

    if (longParts.length === 3) {
      longitude =
        parseInt(longParts[0]) +
        this.convertMinSecToDecimal(
          parseInt(longParts[1]),
          parseInt(longParts[2])
        );

      // West longitudes are negative.
      if (long.includes('W')) {
        longitude *= -1;
      }
    }

    return {
      longitude,
      latitude,
    };
  }

  /** Convert the minutes and seconds of coordinate to decimal. */
  private convertMinSecToDecimal(min: number, sec: number): number {
    const minDec = min / 60;
    const secDec = sec / 3600;

    return minDec + secDec;
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get station info.',
    });
  }
}
